import {Box, Typography} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import ArrDataGridPro from '../../../../common/arr-data-grid-pro/ArrDataGridPro';
import './DocErrorPanel.scss';
import {t} from 'i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {columns} from './DocErrorPanelData';
import {useGetAIReviewDocErrorPanel} from '../../../../../hooks/ai/review/useReview';
import {IGetMasterDocIdForRelevancyPayloadBody} from '../../../../../api/types/aiReview.type';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {IPayload, IPreviewDoc, PAYLOAD_TYPE} from '../JobResults';
import {DataGridRowSelectionModel} from '../../../../../shared/interfaces';
import {ReactComponent as OpenInNew} from '../../../../../assets/images/open-in-new.svg';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

interface IDocErrorPanel {
  isDocErrorPanelOpen: boolean;
  jobId: number | string;
  handleSetStates: (
    payload: IPayload,
    type: PAYLOAD_TYPE,
    docsToView?: number
  ) => void;
  setSelectedDocForPreview: React.Dispatch<
    React.SetStateAction<IPreviewDoc | null>
  >;
}

const DocErrorPanel = ({
  isDocErrorPanelOpen,
  jobId,
  handleSetStates,
  setSelectedDocForPreview,
}: IDocErrorPanel) => {
  const [errorFilter, setErrorFilter] = useState('');
  const [pageSize, setPageSize] = useState<number>(25);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [errorNavigateDocList, setErrorNavigateDocList] = useState<{
    isOpen: boolean;
    message: string;
    status: ToastType;
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [unselectedIds, setUnselectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [showRetryErrorToast, setShowRetryErrorToast] = useState(false);

  const {
    data: reviewDocErrorPanel,
    isLoading,
    isError,
    refetch,
  } = useGetAIReviewDocErrorPanel({
    page: pageNumber,
    pageSize,
    search: errorFilter,
    sortBy: 'docId',
    sortOrder: 'asc',
    reviewJobID: jobId,
  });

  useEffect(() => {
    isDocErrorPanelOpen && setPageNumber(1);
  }, [isDocErrorPanelOpen]);

  const handleRetry = async () => {
    setShowRetryErrorToast(false);
    try {
      const result = await refetch();
      if (result.isError) {
        setShowRetryErrorToast(true);
      }
    } catch {
      setShowRetryErrorToast(true);
    }
  };

  const handleViewInDocList = () => {
    const payloadBody: IGetMasterDocIdForRelevancyPayloadBody = {
      isInverse: isAllSelected,
      selectedDocIDs: !isAllSelected ? selectedIds.join(',') : '',
      excludedDocIDs: isAllSelected ? unselectedIds.join(',') : '',
      documentCount: 0,
      search: errorFilter,
    };
    const totalDocsToView =
      isAllSelected && reviewDocErrorPanel
        ? reviewDocErrorPanel?.total - unselectedIds.length
        : selectedIds.length;
    handleSetStates(
      {payloadBody, jobId},
      PAYLOAD_TYPE.DOC_ERROR,
      totalDocsToView
    );
    return;
  };

  const handleSelectionStateChange = useCallback(
    (state: DataGridRowSelectionModel) => {
      setSelectedIds(state.selectedIds);
      setUnselectedIds(state.unselectedIds);
      setIsAllSelected(state.isAllSelected);
    },
    []
  );
  const handleErrorDocClicked = (props: IPreviewDoc) => {
    setSelectedDocForPreview({...props});
  };

  return (
    <Box className="docs-errored-container">
      {/* Header */}
      <Box className="header">
        <Typography
          className="typo-text headline3_size"
          tabIndex={0}
          data-testid="doc-error-panel-title"
        >
          {t('ai.review.jobErrorPanel.title')}
        </Typography>

        <Box tabIndex={0}>
          <ArrButton
            variant="secondary"
            size="medium"
            disabled={selectedIds?.length === 0 && !isAllSelected}
            aria-label={t(
              'ai.review.groundTruthEvaluation.metrics.viewInDocList'
            )}
            dataTestId="viewInDocList-button"
            onClick={handleViewInDocList}
            endIcon={<OpenInNew />}
          >
            {t('ai.review.groundTruthEvaluation.metrics.viewInDocList')}
          </ArrButton>
        </Box>
      </Box>

      {isError ? (
        <Box
          className="docs-errored-container__error"
          data-testid="doc-error-panel-error"
          role="alert"
          aria-live="assertive"
        >
          <Typography
            component="h3"
            className="typo-text subhead1_size text-headings docs-errored-container__error-title"
            data-testid="doc-error-panel-error-title"
          >
            {t('ai.review.jobErrorPanel.docErrorPanelApiFailureTitle')}
          </Typography>
          <Typography
            className="typo-text body2_size text-body-subtle docs-errored-container__error-description"
            data-testid="doc-error-panel-error-description"
          >
            {t('ai.review.jobErrorPanel.docErrorPanelApiFailureDescription')}
          </Typography>
          <ArrButton
            variant="primary"
            size="small"
            dataTestId="doc-error-panel-error-retry-button"
            onClick={handleRetry}
          >
            {t('ai.review.jobErrorPanel.docErrorPanelApiFailureRetry')}
          </ArrButton>
        </Box>
      ) : (
        <ArrDataGridPro
          className="docs-errored-grid"
          rows={reviewDocErrorPanel?.items ?? []}
          columns={columns({
            rows: reviewDocErrorPanel?.items ?? [],
            handleErrorDocClicked,
          })}
          density="compact"
          showPerPageOptions
          autoHeight
          headerFilters
          pagination
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          itemsPerPage={pageSize}
          setItemsPerPage={setPageSize}
          onPageChange={setPageNumber}
          currentPage={pageNumber}
          disableColumnResize={false}
          rowHeight={60}
          rowCount={reviewDocErrorPanel?.total ?? 0}
          alwaysShowPagination
          hideFooter={false}
          filterMode="server"
          paginationMode="server"
          disableColumnMenu
          loading={isLoading}
          onFilterModelChange={(model) => {
            const value = model.items?.[0]?.value || '';
            setErrorFilter(value);
            setPageNumber(1);
          }}
          entityType={t('common.entries')}
          onSelectionStateChange={handleSelectionStateChange}
          enableCheckbox={true}
          selectedIds={selectedIds}
          unselectedIds={unselectedIds}
          isAllSelected={isAllSelected}
          getRowId={(row) => row.documentID}
          primaryKey="documentID"
        />
      )}

      {showRetryErrorToast && (
        <ArrToastNotification
          message={t('ai.review.jobErrorPanel.docErrorPanelApiFailureToast')}
          onClose={() => setShowRetryErrorToast(false)}
          status={[ToastType.Error]}
        />
      )}
      {errorNavigateDocList?.isOpen && (
        <ArrToastNotification
          message={errorNavigateDocList.message}
          status={[errorNavigateDocList.status]}
          onClose={() =>
            setErrorNavigateDocList((prev) => prev && {...prev, isOpen: false})
          }
          data-testid="toast-navigate-doc-list"
        />
      )}
    </Box>
  );
};

export default DocErrorPanel;
